/**
 * Roles Service
 * 
 * Higher-level operations for role management including:
 * - Role assignment to users/groups
 * - Bulk operations
 * - Role inheritance and scope resolution
 */

import { getSupabaseClient, isSupabaseConfigured } from './supabaseClient';
import { createRoleAssignmentRpc, deleteRoleAssignmentRpc } from './delegationRpc';
import type { ActorRole } from '../types/prioritization/roles';
import type { ScopeKind } from '../types/prioritization/permissions';
import { ROLE_PACKAGES, type RolePackageSlug } from '../types/prioritization/rolePackages';

// ============================================================================
// Types
// ============================================================================

export interface RoleAssignment {
  id: string;
  subjectType: 'user' | 'group';
  subjectId: string;
  roleId: string;
  scopeKind: ScopeKind;
  scopeId: string;
  grantedBy: string;
  grantSource: 'manual' | 'idp' | 'inherited';
  active: boolean;
  expiresAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateRoleAssignmentInput {
  subjectType: 'user' | 'group';
  subjectId: string;
  roleId: string;
  scopeKind: ScopeKind;
  scopeId?: string;
  expiresAt?: Date;
  /** Operating-mode tier (migration 556). 'primary' = anchors UI; 'supporting' = capable but not foregrounded. Defaults to 'supporting'. */
  tier?: 'primary' | 'supporting';
}

export interface RoleWithAssignmentCount {
  id: string;
  name: ActorRole;
  displayName: string;
  assignmentCount: number;
}

// ============================================================================
// Role Assignment CRUD
// ============================================================================

/**
 * Get all role assignments, optionally filtered
 */
export async function getRoleAssignments(filters?: {
  subjectType?: 'user' | 'group';
  subjectId?: string;
  roleId?: string;
  scopeKind?: ScopeKind;
  scopeId?: string;
  activeOnly?: boolean;
}): Promise<RoleAssignment[]> {
  if (!isSupabaseConfigured()) {
    console.warn('[RolesService] Supabase not configured');
    return [];
  }
  
  const supabase = getSupabaseClient();
  let query = supabase.from('role_assignments').select('*');
  
  if (filters?.subjectType) {
    query = query.eq('subject_type', filters.subjectType);
  }
  if (filters?.subjectId) {
    query = query.eq('subject_id', filters.subjectId);
  }
  if (filters?.roleId) {
    query = query.eq('role_id', filters.roleId);
  }
  if (filters?.scopeKind) {
    query = query.eq('scope_kind', filters.scopeKind);
  }
  if (filters?.scopeId) {
    query = query.eq('scope_id', filters.scopeId);
  }
  if (filters?.activeOnly !== false) {
    query = query.eq('active', true);
  }
  
  const { data, error } = await query.order('created_at', { ascending: false });
  
  if (error) {
    console.error('[RolesService] Failed to fetch role assignments:', error);
    throw error;
  }
  
  return (data || []).map(row => ({
    id: row.id,
    subjectType: row.subject_type,
    subjectId: row.subject_id,
    roleId: row.role_id,
    scopeKind: row.scope_kind,
    scopeId: row.scope_id,
    grantedBy: row.granted_by,
    grantSource: row.grant_source,
    active: row.active,
    expiresAt: row.expires_at ? new Date(row.expires_at) : undefined,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
  }));
}

/**
 * Create a new role assignment
 */
export async function createRoleAssignment(
  input: CreateRoleAssignmentInput,
  _grantedBy: string
): Promise<RoleAssignment> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }

  const result = await createRoleAssignmentRpc({
    subjectType: input.subjectType,
    subjectId: input.subjectId,
    roleId: input.roleId,
    scopeKind: input.scopeKind,
    scopeId: input.scopeId || '*',
    expiresAt: input.expiresAt,
    tier: input.tier ?? 'supporting',
  });

  if (!result.success || !result.id) {
    const msg = result.error ?? 'create_role_assignment RPC returned no id';
    console.error('[RolesService] Failed to create role assignment:', msg);
    throw new Error(msg);
  }

  // Fetch the newly-created row so we can return a fully-typed object
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('role_assignments')
    .select('*')
    .eq('id', result.id)
    .single();

  if (error || !data) {
    throw new Error(error?.message ?? 'Could not fetch new role assignment');
  }

  return {
    id: data.id,
    subjectType: data.subject_type,
    subjectId: data.subject_id,
    roleId: data.role_id,
    scopeKind: data.scope_kind,
    scopeId: data.scope_id,
    grantedBy: data.granted_by,
    grantSource: data.grant_source,
    active: data.active,
    expiresAt: data.expires_at ? new Date(data.expires_at) : undefined,
    createdAt: new Date(data.created_at),
    updatedAt: new Date(data.updated_at),
  };
}

/**
 * Revoke (deactivate) a role assignment
 */
export async function revokeRoleAssignment(assignmentId: string): Promise<void> {
  // UPDATE is also blocked by RLS; use the delete RPC (hard-delete) instead.
  return deleteRoleAssignment(assignmentId);
}

/**
 * Delete a role assignment permanently
 */
export async function deleteRoleAssignment(assignmentId: string): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }

  const result = await deleteRoleAssignmentRpc(assignmentId);
  if (!result.success) {
    const msg = result.error ?? 'delete_role_assignment RPC failed';
    console.error('[RolesService] Failed to delete role assignment:', msg);
    throw new Error(msg);
  }
}

/**
 * Update a role assignment (e.g., change scope)
 */
export async function updateRoleAssignment(
  assignmentId: string,
  updates: { scopeKind?: ScopeKind; scopeId?: string; active?: boolean }
): Promise<RoleAssignment> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }
  
  const supabase = getSupabaseClient();
  
  const updateData: Record<string, unknown> = {
    updated_at: new Date().toISOString(),
  };
  
  if (updates.scopeKind !== undefined) {
    updateData.scope_kind = updates.scopeKind;
  }
  if (updates.scopeId !== undefined) {
    updateData.scope_id = updates.scopeId;
  }
  if (updates.active !== undefined) {
    updateData.active = updates.active;
  }
  
  const { data, error } = await supabase
    .from('role_assignments')
    .update(updateData)
    .eq('id', assignmentId)
    .select()
    .single();
  
  if (error) {
    console.error('[RolesService] Failed to update role assignment:', error);
    throw error;
  }
  
  return {
    id: data.id,
    subjectType: data.subject_type,
    subjectId: data.subject_id,
    roleId: data.role_id,
    scopeKind: data.scope_kind,
    scopeId: data.scope_id,
    grantedBy: data.granted_by,
    grantSource: data.grant_source,
    active: data.active,
    expiresAt: data.expires_at ? new Date(data.expires_at) : undefined,
    createdAt: new Date(data.created_at),
    updatedAt: new Date(data.updated_at),
  };
}

// ============================================================================
// User Role Queries
// ============================================================================

/**
 * Get all roles assigned to a specific user
 */
export async function getUserRoles(userId: string): Promise<RoleAssignment[]> {
  return getRoleAssignments({ subjectType: 'user', subjectId: userId, activeOnly: true });
}

/**
 * Get all roles assigned to a specific group
 */
export async function getGroupRoles(groupId: string): Promise<RoleAssignment[]> {
  return getRoleAssignments({ subjectType: 'group', subjectId: groupId, activeOnly: true });
}

/**
 * Get all users/groups with a specific role
 */
export async function getRoleHolders(roleId: string): Promise<RoleAssignment[]> {
  return getRoleAssignments({ roleId, activeOnly: true });
}

// ============================================================================
// Bulk Operations
// ============================================================================

/**
 * Assign a role to multiple users at once
 */
export async function bulkAssignRole(
  roleId: string,
  userIds: string[],
  scopeKind: ScopeKind,
  scopeId: string,
  grantedBy: string
): Promise<{ success: number; failed: number; errors: string[] }> {
  const results = { success: 0, failed: 0, errors: [] as string[] };
  
  for (const userId of userIds) {
    try {
      await createRoleAssignment(
        { subjectType: 'user', subjectId: userId, roleId, scopeKind, scopeId },
        grantedBy
      );
      results.success++;
    } catch (error) {
      results.failed++;
      results.errors.push(`User ${userId}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  return results;
}

/**
 * Revoke a role from multiple users at once
 */
export async function bulkRevokeRole(
  roleId: string,
  userIds: string[]
): Promise<{ success: number; failed: number; errors: string[] }> {
  const results = { success: 0, failed: 0, errors: [] as string[] };
  
  for (const userId of userIds) {
    try {
      const assignments = await getRoleAssignments({
        subjectType: 'user',
        subjectId: userId,
        roleId,
        activeOnly: true,
      });
      
      for (const assignment of assignments) {
        await revokeRoleAssignment(assignment.id);
      }
      results.success++;
    } catch (error) {
      results.failed++;
      results.errors.push(`User ${userId}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  return results;
}

// ============================================================================
// Analytics
// ============================================================================

/**
 * Get role usage statistics
 */
export async function getRoleUsageStats(): Promise<RoleWithAssignmentCount[]> {
  if (!isSupabaseConfigured()) {
    return [];
  }
  
  const supabase = getSupabaseClient();
  
  // Get all roles
  const { data: roles, error: rolesError } = await supabase
    .from('roles')
    .select('id, name, display_name');
  
  if (rolesError) {
    console.error('[RolesService] Failed to fetch roles:', rolesError);
    return [];
  }
  
  // Get assignment counts
  const { data: assignments, error: assignmentsError } = await supabase
    .from('role_assignments')
    .select('role_id')
    .eq('active', true);
  
  if (assignmentsError) {
    console.error('[RolesService] Failed to fetch assignments:', assignmentsError);
    return [];
  }
  
  // Count assignments per role
  const counts = new Map<string, number>();
  for (const assignment of assignments || []) {
    const current = counts.get(assignment.role_id) || 0;
    counts.set(assignment.role_id, current + 1);
  }
  
  return (roles || []).map(role => ({
    id: role.id,
    name: role.name as ActorRole,
    displayName: role.display_name,
    assignmentCount: counts.get(role.id) || 0,
  }));
}

// ============================================================================
// Clone-on-Create Support
// ============================================================================

/**
 * A single active role assignment carried over from a clone source user,
 * preserving its own scope exactly (individual roles cloned this way are
 * NOT forced through a single shared scope — see fetchUserAssignments).
 */
export interface ClonedIndividualRole {
  roleId: string;
  roleLabel: string;
  /** Matches the `p_scope_kind` union accepted by create_role_assignment. */
  scopeKind: 'global' | 'organization' | 'group' | 'resource';
  scopeId: string;
}

export interface ClonedUserAssignments {
  /** Set when the user's active role set exactly matches one known package (see below). */
  packageSlug: RolePackageSlug | null;
  groupIds: string[];
  /** Active role assignments not covered by `packageSlug`. */
  individualRoles: ClonedIndividualRole[];
  orgUnitId: string | null;
}

/**
 * Fetch a user's current active role/group/org-unit assignments so they can be
 * used to pre-populate the "clone user" flow in the Create User modal.
 *
 * Package inference: a package "matches" when every role slug it defines is
 * present in the user's active role assignments. If more than one package
 * matches (ambiguous), we deliberately do NOT guess — every held role is
 * surfaced as an individual role instead. This mirrors the existing package
 * model: packages are not a DB entity, just a named bundle of role slugs
 * (see rolePackages.ts), so "cloned from package X" is always an inference,
 * never a stored fact.
 */
export async function fetchUserAssignments(userId: string): Promise<ClonedUserAssignments> {
  const empty: ClonedUserAssignments = { packageSlug: null, groupIds: [], individualRoles: [], orgUnitId: null };

  if (!isSupabaseConfigured()) {
    console.warn('[RolesService] Supabase not configured');
    return empty;
  }

  const supabase = getSupabaseClient();

  const [raResult, membershipsResult, nodeResult] = await Promise.all([
    supabase
      .from('role_assignments')
      .select('id, role_id, scope_kind, scope_id, roles:role_id(id, name, display_name)')
      .eq('subject_type', 'user')
      .eq('subject_id', userId)
      .eq('active', true),
    supabase.from('memberships').select('group_id').eq('user_id', userId),
    supabase
      .from('user_node_assignments')
      .select('org_unit_id')
      .eq('user_id', userId)
      .eq('active', true)
      .limit(1),
  ]);

  if (raResult.error) {
    console.error('[RolesService] fetchUserAssignments: role_assignments query failed:', raResult.error);
  }
  if (membershipsResult.error) {
    console.error('[RolesService] fetchUserAssignments: memberships query failed:', membershipsResult.error);
  }
  if (nodeResult.error) {
    console.error('[RolesService] fetchUserAssignments: user_node_assignments query failed:', nodeResult.error);
  }

  type RoleAssignmentRow = {
    id: string;
    role_id: string;
    scope_kind: ClonedIndividualRole['scopeKind'];
    scope_id: string;
    roles: { id: string; name: string; display_name: string | null } | null;
  };

  const raRows = ((raResult.data as unknown as RoleAssignmentRow[]) || []).filter((row) => !!row.roles?.name);

  // Map role slug -> the assignment row that carries it (dedupes multiple
  // scoped assignments of the same role down to one entry for cloning purposes).
  const slugToRow = new Map<string, RoleAssignmentRow>();
  for (const row of raRows) {
    slugToRow.set(row.roles!.name, row);
  }
  const heldSlugs = new Set(slugToRow.keys());

  const candidates = ROLE_PACKAGES.filter(
    (pkg) => pkg.roleSlug.length > 0 && pkg.roleSlug.every((slug) => heldSlugs.has(slug)),
  );
  const matchedPkg = candidates.length === 1 ? candidates[0] : null;
  const coveredSlugs = new Set(matchedPkg ? matchedPkg.roleSlug : []);

  const individualRoles: ClonedIndividualRole[] = [];
  for (const [slug, row] of slugToRow.entries()) {
    if (coveredSlugs.has(slug)) continue;
    individualRoles.push({
      roleId: row.role_id,
      roleLabel: row.roles?.display_name || slug,
      scopeKind: row.scope_kind,
      scopeId: row.scope_id,
    });
  }

  const groupIds = [...new Set((membershipsResult.data || []).map((m: { group_id: string }) => m.group_id))];
  const orgUnitId = nodeResult.data && nodeResult.data.length > 0 ? (nodeResult.data[0] as { org_unit_id: string }).org_unit_id : null;

  return {
    packageSlug: matchedPkg?.slug ?? null,
    groupIds,
    individualRoles,
    orgUnitId,
  };
}

/**
 * Check if a user has a specific role at a scope
 */
export async function userHasRole(
  userId: string,
  roleName: ActorRole,
  scopeKind?: ScopeKind,
  scopeId?: string
): Promise<boolean> {
  if (!isSupabaseConfigured()) {
    return false;
  }
  
  const supabase = getSupabaseClient();
  
  // First get the role ID
  const { data: role } = await supabase
    .from('roles')
    .select('id')
    .eq('name', roleName)
    .single();
  
  if (!role) {
    return false;
  }
  
  // Check for assignment
  let query = supabase
    .from('role_assignments')
    .select('id')
    .eq('subject_type', 'user')
    .eq('subject_id', userId)
    .eq('role_id', role.id)
    .eq('active', true);
  
  if (scopeKind) {
    query = query.eq('scope_kind', scopeKind);
  }
  if (scopeId) {
    query = query.or(`scope_id.eq.${scopeId},scope_id.eq.*`);
  }
  
  const { data } = await query.limit(1);
  
  return (data?.length || 0) > 0;
}
